import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Ban, Trash2, RefreshCw, Plus, ChevronLeft, ChevronRight, Download } from 'lucide-react';

interface Provider {
  id: string;
  name: string;
  is_active: number;
}

interface BlockedContact {
  email: string;
  senderEmail: string;
  reason: { code: string; message: string };
  blockedAt: string;
  marketingBlocked?: boolean;
  transactionalBlocked?: boolean;
}

export default function Blocklist() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [contacts, setContacts] = useState<BlockedContact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [source, setSource] = useState<'all' | 'smtp' | 'blacklisted'>('all');

  // Add form
  const [emailsText, setEmailsText] = useState('');
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState<{ success: boolean; message: string } | null>(null);

  // Sync
  const [syncing, setSyncing] = useState(false);

  // Remove
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      fetchBlocklist();
    }
  }, [selectedProvider, offset, source]);

  const fetchProviders = async () => {
    try {
      const data = await api.getBrevoProviders();
      setProviders(data.providers);
      // Автовыбор активного провайдера
      const active = data.providers.find(p => p.is_active === 1);
      if (active) {
        setSelectedProvider(active.id);
      } else if (data.providers.length > 0) {
        setSelectedProvider(data.providers[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    } finally {
      setLoadingProviders(false);
    }
  };

  const fetchBlocklist = async () => {
    if (!selectedProvider) return;
    setLoading(true);
    try {
      const data = await api.getBlocklist(selectedProvider, { limit, offset, source });
      setContacts(data.contacts);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch blocklist:', err);
    } finally {
      setLoading(false);
    }
  };

  const MAX_EMAILS = 50000;

  const handleAdd = async () => {
    if (!selectedProvider || !emailsText.trim()) return;

    const emails = emailsText
      .split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e.length > 0);

    if (emails.length === 0) {
      setAddResult({ success: false, message: 'No emails provided' });
      return;
    }

    if (emails.length > MAX_EMAILS) {
      setAddResult({ success: false, message: `Maximum ${MAX_EMAILS.toLocaleString()} emails per operation.` });
      return;
    }

    setAdding(true);
    setAddResult(null);

    try {
      const result = await api.addToBlocklist(selectedProvider, emails);
      setAddResult({ success: true, message: `${result.added.toLocaleString()} emails sent to blocklist (async import)` });
      setEmailsText('');
      fetchBlocklist();
    } catch (err) {
      setAddResult({ success: false, message: err instanceof Error ? err.message : 'Failed to add' });
    } finally {
      setAdding(false);
      }
  };

  const handleSync = async () => {
    if (!selectedProvider) return;
    setSyncing(true);
    try {
      const result = await api.backfillBlocklist(selectedProvider);
      setAddResult({ success: true, message: `Synced ${result.synced.toLocaleString()} contacts from Brevo` });
      fetchBlocklist();
    } catch (err) {
      setAddResult({ success: false, message: err instanceof Error ? err.message : 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleRemove = async (email: string) => {
    if (!selectedProvider) return;
    setRemoving(email);
    try {
      await api.removeFromBlocklist(selectedProvider, email);
      // Обновить список
      fetchBlocklist();
    } catch (err) {
      console.error('Failed to remove:', err);
    } finally {
      setRemoving(null);
    }
  };

  const emailCount = emailsText
    .split(/[\n,;]+/)
    .map(e => e.trim())
    .filter(e => e.length > 0).length;


  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  if (loadingProviders) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="space-y-8">
        <h1 className="text-3xl font-bold">Blocklist</h1>
        <Alert>
          <AlertDescription>
            No Brevo providers configured. Please add a Brevo provider in Settings first.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold">Blocklist</h1>
          <Ban className="h-8 w-8 text-muted-foreground" />
        </div>
      </div>

      {/* Provider Select */}
      <Card>
        <CardHeader>
          <CardTitle>Brevo Account</CardTitle>
          <CardDescription>Select the Brevo account to manage blocklist</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedProvider} onValueChange={(v) => { setSelectedProvider(v); setOffset(0); }}>
            <SelectTrigger className="w-80">
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                  {p.is_active === 1 && <Badge variant="secondary" className="ml-2">active</Badge>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Add to Blocklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add to Blocklist
          </CardTitle>
          <CardDescription>
            Add emails to blocklist. Enter one email per line, or separate with commas.
            Maximum 50,000 emails. Import is asynchronous, contacts may take a few minutes to appear.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Textarea
              placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
              value={emailsText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEmailsText(e.target.value)}
              rows={6}
              className="font-mono text-sm"
            />
            <p className="text-sm text-muted-foreground mt-2">
              {emailCount.toLocaleString()} email{emailCount !== 1 ? 's' : ''} entered
              {emailCount > MAX_EMAILS && <span className="text-destructive"> (max {MAX_EMAILS.toLocaleString()})</span>}
            </p>
          </div>

          {addResult && (
            <Alert variant={addResult.success ? 'default' : 'destructive'}>
              <AlertDescription>{addResult.message}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={handleAdd}
            disabled={adding || !emailsText.trim() || emailCount > MAX_EMAILS}
          >
            {adding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {adding
              ? `Uploading...`
              : `Add ${emailCount > 0 ? `${emailCount.toLocaleString()} email${emailCount !== 1 ? 's' : ''}` : 'to Blocklist'}`
            }
          </Button>
        </CardContent>
      </Card>

      {/* Blocked Contacts List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Blocked Contacts</CardTitle>
              <CardDescription>
                {total > 0 ? `${total.toLocaleString()} contacts in blocklist` : 'No blocked contacts'}
              </CardDescription>
              <div className="flex gap-2 mt-2">
                {(['all', 'smtp', 'blacklisted'] as const).map(s => (
                  <Button
                    key={s}
                    variant={source === s ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSource(s); setOffset(0); }}
                  >
                    {s === 'all' ? 'All' : s === 'smtp' ? 'SMTP Blocked' : 'Blacklisted'}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing || !selectedProvider}>
                <Download className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync from Brevo'}
              </Button>
              <Button variant="outline" size="sm" onClick={fetchBlocklist} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : contacts.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No blocked contacts found</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center">Marketing</TableHead>
                    <TableHead className="text-center">Transactional</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Blocked At</TableHead>
                    <TableHead className="w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.email}>
                      <TableCell className="font-mono text-sm">{contact.email}</TableCell>
                      <TableCell className="text-center">
                        {contact.marketingBlocked === true && <Badge variant="destructive">Blocked</Badge>}
                        {contact.marketingBlocked === undefined && <span className="text-xs text-muted-foreground">--</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {contact.transactionalBlocked === true && <Badge variant="destructive">Blocked</Badge>}
                        {contact.transactionalBlocked === undefined && <span className="text-xs text-muted-foreground">--</span>}
                      </TableCell>
                      <TableCell>
                        {contact.reason.code && <Badge variant="outline">{contact.reason.code}</Badge>}
                        {contact.reason.message && <span className="text-xs text-muted-foreground ml-2">{contact.reason.message}</span>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(contact.blockedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(contact.email)}
                          disabled={removing === contact.email}
                          title="Remove from blocklist"
                        >
                          {removing === contact.email ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-destructive" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                      disabled={offset === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOffset(offset + limit)}
                      disabled={offset + limit >= total}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
